// Settings — a modal sheet opened from the user menu. Left rail of
// section tabs, right pane of content. ESC closes; backdrop click
// closes; the sections themselves own their save state.
//
// Phase 1 surfaces:
//   • Account   — read-only display of user info + stubs for 2FA /
//                 device tokens / secrets (Phase 2 modals)
//   • Appearance — same controls as the floating Tweaks panel,
//                  mirrored here for users who don't know the FAB
//   • Image gen — full image-gen prefs (Quality/Style/Aspect + the
//                 Advanced overrides the server supports)

import { useEffect, useState } from "preact/hooks";
import "../../styles/settings.css";

import { Icon, type IconName } from "../../icons";
import type { Me } from "../../lib/use-me";
import type { Tweaks } from "../../lib/use-tweaks";
import { SettingsAccount } from "./account";
import { SettingsAlarms } from "./alarms";
import { SettingsAppearance } from "./appearance";
import { SettingsDiagnostics } from "./diagnostics";
import { SettingsImageGen } from "./image-gen";

interface SettingsViewProps {
  open: boolean;
  onClose: () => void;
  me: Me | null;
  tweaks: Tweaks;
  setTweak: <K extends keyof Tweaks>(key: K, value: Tweaks[K]) => void;
  notify: (msg: string) => void;
  onOpenTotp: () => void;
  onOpenDeviceTokens: () => void;
  onOpenSecrets: () => void;
  onOpenDownloads: () => void;
}

// Households moved into the Family hub (avatar menu → Family) — no longer a
// Settings section.
type Section = "account" | "appearance" | "alarms" | "image-gen" | "diagnostics";

interface NavItem {
  id: Section;
  label: string;
  icon: IconName;
}

const NAV: NavItem[] = [
  { id: "account",    label: "Account",    icon: "people" },
  { id: "appearance", label: "Appearance", icon: "sparkle" },
  { id: "alarms",     label: "Alarms",     icon: "alarm" },
  { id: "image-gen",  label: "Image gen",  icon: "image" },
  { id: "diagnostics",label: "Diagnostics",icon: "settings" },
];

export function SettingsView({
  open, onClose, me, tweaks, setTweak, notify,
  onOpenTotp, onOpenDeviceTokens, onOpenSecrets, onOpenDownloads,
}: SettingsViewProps) {
  const [section, setSection] = useState<Section>("account");
  // Mobile drill-in: on a narrow screen the sheet shows the section
  // LIST first; tapping a section reveals its content with a back row.
  // On desktop both panes show side-by-side and this flag is ignored
  // (CSS only acts on it under the mobile media query).
  const [entered, setEntered] = useState(false);
  const activeNav = NAV.find((n) => n.id === section);

  // Reset to the section list each time the sheet (re)opens so we don't
  // land the user on whatever section they hit last week.
  useEffect(() => {
    if (open) { setSection("account"); setEntered(false); }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="settings-modal-bg" onClick={onClose}>
      <div className="settings-sheet" data-entered={entered} onClick={(e) => e.stopPropagation()} role="dialog" aria-label="Settings">
        <aside className="settings-nav">
          <div className="settings-nav-head">
            <h2 className="settings-nav-title">Settings</h2>
            <button className="icon-btn" onClick={onClose} title="Close" type="button">
              <Icon name="x" size={14} />
            </button>
          </div>
          <nav className="settings-nav-list">
            {NAV.map((n) => (
              <button
                key={n.id}
                className="settings-nav-item"
                data-active={section === n.id}
                onClick={() => { setSection(n.id); setEntered(true); }}
                type="button"
              >
                <Icon name={n.icon} size={14} />
                <span>{n.label}</span>
                <span className="settings-nav-chev" aria-hidden><Icon name="arrow-right" size={14} /></span>
              </button>
            ))}
          </nav>
        </aside>

        <div className="settings-content">
          {/* Mobile-only back row — returns to the section list. Hidden on
              desktop (both panes are always visible there). */}
          <button className="settings-back" type="button" onClick={() => setEntered(false)}>
            <Icon name="arrow-left" size={16} />
            <span>{activeNav?.label ?? "Settings"}</span>
          </button>
          {section === "account"    && (
            <SettingsAccount
              me={me}
              notify={notify}
              onOpenTotp={() => { onClose(); onOpenTotp(); }}
              onOpenDeviceTokens={() => { onClose(); onOpenDeviceTokens(); }}
              onOpenSecrets={() => { onClose(); onOpenSecrets(); }}
              onOpenDownloads={() => { onClose(); onOpenDownloads(); }}
            />
          )}
          {section === "appearance" && <SettingsAppearance tweaks={tweaks} setTweak={setTweak} />}
          {section === "alarms"     && <SettingsAlarms notify={notify} />}
          {section === "image-gen"  && <SettingsImageGen notify={notify} />}
          {section === "diagnostics"&& <SettingsDiagnostics notify={notify} />}
        </div>
      </div>
    </div>
  );
}
